import { Component, CreateEffectOptions, effect, inject, input, signal } from '@angular/core';
import { AnalyticsService, WidgetData } from '../../../../../core/services/analytics.service';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import { filter, Observable, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MetricCardComponent } from '../../../../../core/components/metric-card/metric-card.component';
import { CommonModule } from '@angular/common';
import { getPastDaysDateRange } from '../../../../../core/shared/common/date-range.util';

@Component({
  selector: 'app-mttd-widget',
  standalone: true,
  imports: [MetricCardComponent, CommonModule],
  templateUrl: './mttd-widget.component.html',
  styleUrl: './mttd-widget.component.scss',
  providers: [AnalyticsService],
})
export class MttdWidgetComponent extends BaseComponent {
  private route = inject(ActivatedRoute);
  private analyticsService = inject(AnalyticsService);

  public useDateRange = input<boolean>();
  public dateRange = input<any>(false);

  public mttdData = signal<WidgetData>({ value: 0, colors: [] });
  public isLoading = signal<boolean>(true);

  // For the metric card
  public cardTitle = 'MTTD';
  public suffix = ' mins';

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    this.route.queryParams
      .pipe(
        filter((params) => !!params['organizationId']),
        takeUntil(this.destroyed$)
      )
      .subscribe((params) => {
        this.loadData(params);
      });

    effect(
      () => {
        this.dateRange();
        // When date range changes, reload data if we have an organization ID
        if (this.route.snapshot.queryParams['organizationId']) {
          this.loadData(this.route.snapshot.queryParams);
        }
      },
      { allowSignalWrites: true } as CreateEffectOptions
    );
  }

  private loadData(params: any) {
    this.isLoading.set(true);
    const dateRange = this.dateRange();

    this.getMttdData(params, dateRange).subscribe({
      next: (data) => {
        this.mttdData.set(data);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error(error);
        this.isLoading.set(false);
      },
    });
  }

  private getMttdData(params: any, dateRange: any) {
    if (dateRange) {
      return this.analyticsService.getMttd(params['organizationId'], dateRange);
    } else if (!this.useDateRange()) {
      // Default to rolling 30-day window for statistical stability
      const defaultDateRange = getPastDaysDateRange(30);
      return this.analyticsService.getMttd(params['organizationId'], defaultDateRange);
    }

    return new Observable<WidgetData>();
  }
}
